<template>
  <div
    v-if="items.length"
    class="items"
    data-locator="mention-list"
    :style="{ '--mention-max-height': computedMaxHeight }"
  >
    <template v-for="(item, index) in items" :key="item.mentionId || `${item.id}-${index}`">
      <div
        v-if="item.name"
        class="item item-group no-click-outside"
        :class="{ 'is-selected': index === selectedIndex }"
        data-locator="mention-list-group-item"
        :data-mention-id="`group-${item.id}`"
        @mouseenter="selectedIndex = index"
        @click="selectItem(index, 'group', item.id)"
      >
        <app-avatar
          size="xs"
          default-icon="group"
          color="#FF5D35"
          border-color="#FFDFD7"
          background-color="#FFEFEB"
        />

        <div class="item-group__content">
          <span class="item-group__label">
            {{ item.name }}
          </span>
        </div>
      </div>

      <div
        v-else-if="item.fullName"
        class="item item-member no-click-outside"
        :class="{ 'is-selected': index === selectedIndex }"
        data-locator="mention-list-user-item"
        :data-mention-id="`user-${item.id}`"
        @mouseenter="selectedIndex = index"
        @click="selectItem(index, 'user', item.id)"
      >
        <app-avatar
          :initials="item.initials || item.shortInitials"
          size="xs"
          color="#FF5D35"
          border-color="#FFDFD7"
          background-color="#FFEFEB"
        />

        <div class="item-member__content">
          <div class="item-member__line">
            <span class="item-member__name">
              {{ item.fullName }}
            </span>

            <span
              v-if="item.groupLabel"
              class="item-member__group"
            >
              — {{ item.groupLabel }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'MentionList',
  props: {
    items: {
      type: Array,
      required: true,
    },
    command: {
      type: Function,
      required: true,
    },
    maxVisibleItems: {
      type: Number,
      default: 5,
    },
  },
  data () {
    return {
      selectedIndex: 0,
    }
  },
  computed: {
    computedMaxHeight () {
      return `${this.maxVisibleItems * 40}px`
    },
  },
  watch: {
    items () {
      this.selectedIndex = 0
    },
  },
  methods: {
    onKeyDown ({ event }) {
      if (!this.items.length) return false

      if (event.key === 'ArrowUp') {
        this.upHandler()
        return true
      }
      if (event.key === 'ArrowDown') {
        this.downHandler()
        return true
      }
      if (event.key === 'Enter') {
        this.enterHandler()
        return true
      }
      return false
    },
    upHandler () {
      if (!this.items.length) return
      this.selectedIndex = ((this.selectedIndex + this.items.length) - 1) % this.items.length
    },
    downHandler () {
      if (!this.items.length) return
      this.selectedIndex = (this.selectedIndex + 1) % this.items.length
    },
    enterHandler () {
      const item = this.items[this.selectedIndex]
      if (!item) return

      if (item.fullName) {
        this.selectItem(this.selectedIndex, 'user', item.id)
      } else {
        this.selectItem(this.selectedIndex, 'group', item.id)
      }
    },
    selectItem (index, type, id) {
      const item = this.items[index]
      if (!item) return

      this.command({
        id: `${type}-${id}`,
        label: item.rawFullName || item.fullName || item.name,
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/box-shadow-tokens";

.items {
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
  border: get-border();
  @include border-radius('border-radius-3');
  @include box-shadow('box-shadow-medium');
  max-height: var(--mention-max-height);
  padding: 8px;
}

.item {
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  @include border-radius('border-radius-3');

  &.is-selected,
  &:hover {
    background: #0B0C0D0D;
  }
}

.item-group,
.item-member {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 4px 8px;
}

.item-group {
  &__content {
    min-width: 0;
    flex: 1;
  }

  &__label {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: rgb(var(--v-theme-grey-900));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.item-member {
  &__avatar {
    flex-shrink: 0;
  }

  &__content {
    min-width: 0;
    flex: 1;
  }

  &__line {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
  }

  &__name {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: rgb(var(--v-theme-grey-800));
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__group {
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
    color: rgb(var(--v-theme-grey-500));
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
  }
}
</style>
